import { useCallback } from 'react';
import { format } from 'date-fns';
import { isEmpty } from 'ramda';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { SidebarInfo, SidebarInfoColumn } from 'components';
import Sidebar from 'components/atoms/GefMui/Sidebar/Sidebar';
import SidebarSection from 'components/atoms/GefMui/SidebarSection';
import { EMPTY_VALUES } from 'constants/general';
import contactList from 'features/views/contactList';
import { currency, equalOrThreeDashes, mailToLink, phoneToLink } from 'utils';

export const ContactsSidebar = () => {
	const selectedContact = useSelector(contactList.selectors.getSelectedContact) || EMPTY_VALUES.OBJECT;
	const { client = {}, solutionStateId, hasBrutto, contractEndDate } = selectedContact;
	const { firstname, surname, businessName, mobile, phone, email, address = {} } = client;
	const { city, street, streetNo, zipCode } = address;
	const fullStreet = `${street || ''} ${streetNo || ''}`.trim();

	const isContactInSuccessfullState = useSelector((state) =>
		contactList.selectors.getIsContactInSuccessfullState(state, solutionStateId),
	);

	const handleContractEndDate = useCallback((contractEndDate) => {
		if (contractEndDate) {
			return format(new Date(contractEndDate), 'dd.MM.yyyy');
		} else {
			return equalOrThreeDashes();
		}
	}, []);

	return (
		<Sidebar>
			{isEmpty(selectedContact) || (
				<Sidebar.Body spacing={2} sx={{ p: 3 }}>
					<SidebarSection heading={getLabelMessage('contacts:client')}>
						<SidebarInfo>
							<SidebarInfoColumn
								attribute={getLabelMessage('contacts:businessName')}
								value={equalOrThreeDashes(businessName)}
							/>
							<SidebarInfoColumn
								attribute={getLabelMessage('contacts:name')}
								value={equalOrThreeDashes(firstname)}
							/>
							<SidebarInfoColumn
								attribute={getLabelMessage('contacts:surname')}
								value={equalOrThreeDashes(surname)}
							/>
							<SidebarInfoColumn
								attribute={getLabelMessage('contacts:street')}
								value={equalOrThreeDashes(fullStreet)}
							/>
							<SidebarInfoColumn
								attribute={getLabelMessage('contacts:city')}
								value={equalOrThreeDashes(city)}
							/>
							<SidebarInfoColumn
								attribute={getLabelMessage('contacts:zipCode')}
								value={equalOrThreeDashes(zipCode)}
							/>
							<SidebarInfoColumn
								attribute={getLabelMessage('contacts:phone1')}
								value={phoneToLink(mobile, true)}
							/>
							<SidebarInfoColumn
								attribute={getLabelMessage('contacts:phone2')}
								value={phoneToLink(phone, true)}
							/>
							<SidebarInfoColumn
								attribute={getLabelMessage('contacts:email')}
								value={mailToLink(email, true)}
							/>
						</SidebarInfo>
					</SidebarSection>

					{isContactInSuccessfullState && (
						//  default 0 values for prodBp a prodJp - only for contacts where hasBrutto is true
						<SidebarSection heading={getLabelMessage('contacts:production')}>
							<SidebarInfo>
								<SidebarInfoColumn
									attribute={getLabelMessage('contacts:regular_insurance')}
									value={
										!hasBrutto
											? equalOrThreeDashes(currency(selectedContact.prodBp))
											: equalOrThreeDashes(currency(selectedContact.prodBp || 0))
									}
								/>
								<SidebarInfoColumn
									attribute={getLabelMessage('contacts:single_insurance')}
									value={
										!hasBrutto
											? equalOrThreeDashes(currency(selectedContact.prodJp))
											: equalOrThreeDashes(currency(selectedContact.prodJp || 0))
									}
								/>
							</SidebarInfo>
						</SidebarSection>
					)}

					<SidebarSection heading={getLabelMessage('contacts:contractEndDate')}>
						<SidebarInfo>
							<SidebarInfoColumn
								attribute={getLabelMessage('contacts:date')}
								value={handleContractEndDate(contractEndDate)}
							/>
						</SidebarInfo>
					</SidebarSection>
				</Sidebar.Body>
			)}
		</Sidebar>
	);
};

export default ContactsSidebar;
